import type { TextPromptWithInputItem } from '@/app/types';
import type { ClawDraftFileType, ClawSendFile, UploadClawFileResult } from '../../../../types';
import {
  type DraftAttachmentBase,
  inferFileTypeFromPath,
  isPendingUploadAttachment,
  QUICK_CASES_FOLDER,
} from './quick-prompt';

export interface DraftAttachment extends DraftAttachmentBase {
  /** 欢迎页 pending 上传时记录选文件当刻的目标目录。 */
  targetFolderPath?: string;
}

export interface PendingUploadAttachment {
  file: File;
  tempFilePath: string;
  targetFolderPath?: string;
}

export interface InsertAssetMentionArg {
  path: string;
  name: string;
  type?: ClawDraftFileType;
  previewUrl?: string;
}

/** preview_url 只有 blob: 前缀才需要 revoke；其它（quick case 远端 url）直接放掉。 */
export const revokeBlobPreview = (file: UploadClawFileResult) => {
  if (file.preview_url?.startsWith('blob:')) URL.revokeObjectURL(file.preview_url);
};

/** ChatInput 内部走完上传后，把 UploadClawFileResult 包成 DraftAttachment（remote 可删）。 */
export const makeUploadAttachment = (data: UploadClawFileResult, sourceFile?: File): DraftAttachment => {
  const isVisual = data.file_type === 'image' || data.file_type === 'video';
  return {
    ...data,
    file_name: sourceFile?.name || data.file_name,
    preview_url: sourceFile && isVisual ? URL.createObjectURL(sourceFile) : undefined,
    removable: 'remote',
  };
};

/** 已经在 quick_cases 目录里的 case 资源直接做"引用型"附件，删除时只清本地草稿。 */
export const makeQuickRefAttachment = (data: UploadClawFileResult, fallbackPreview?: string): DraftAttachment => {
  const isVisual = data.file_type === 'image' || data.file_type === 'video';
  return {
    ...data,
    preview_url: isVisual ? fallbackPreview : undefined,
    removable: 'local',
    source: 'quick',
  };
};

interface MakeQuickPendingAttachmentArgs {
  file: File;
  fileName: string;
  fileType: ClawDraftFileType;
  previewUrl?: string;
  tempPath: string;
  targetFolderPath?: string;
}

/** ready 欢迎页的 quick case：暂存 raw File，发送时再上传。 */
export const makeQuickPendingAttachment = ({
  file,
  fileName,
  fileType,
  previewUrl,
  tempPath,
  targetFolderPath,
}: MakeQuickPendingAttachmentArgs): DraftAttachment => ({
  file_path: tempPath,
  file_name: fileName,
  file_type: fileType,
  preview_url: previewUrl,
  removable: 'local',
  rawFile: file,
  source: 'quick',
  targetFolderPath,
});

/** 通过 @ 引用已有 claw 文件：用 inferFileTypeFromPath 兜底类型。 */
export const makeMentionAttachment = (asset: InsertAssetMentionArg): DraftAttachment => {
  const filePath = asset.path.trim();
  return {
    file_path: filePath,
    file_name: asset.name,
    file_type: asset.type ?? inferFileTypeFromPath(filePath),
    preview_url: asset.previewUrl,
    removable: 'local',
  };
};

/**
 * 一个附件卡片在编辑器里对应的 mention id：
 * - quick-preview://upload/ 开头（欢迎页用户上传的临时附件）：mention id === file_path
 * - quick-preview:// 开头但非 upload/（quick case 展示型附件）：mention 由 parseQuickPromptToInputValue
 *   按 `media/quick_cases/<filename>` 写入；用 file_name 反推。
 * - 其它（已上传 / @ 引用）：mention id === file_path。
 */
export const mentionIdForAttachment = (file: DraftAttachment): string => {
  if (file.file_path.startsWith('quick-preview://upload/')) {
    return file.file_path;
  }
  if (file.file_path.startsWith('quick-preview://')) {
    return `${QUICK_CASES_FOLDER}/${file.file_name}`;
  }
  return file.file_path;
};

/** quick prompt / pending 上传补传完成后，把编辑器里的临时 id 替换为真实 file_path。 */
export const replaceMentionIdsInTextPrompt = (
  value: TextPromptWithInputItem,
  idByFileName: Map<string, string>,
): TextPromptWithInputItem => {
  if (!idByFileName.size) return value;
  return value.map((item) => {
    if (item.type === 'mention') {
      const nextId = idByFileName.get(item.mention.name);
      if (!nextId) return item;
      return {
        ...item,
        mention: {
          ...item.mention,
          id: nextId,
        },
      };
    }

    if (item.type === 'claw-template-token' && item.clawTemplateToken.kind === 'upload') {
      const files = item.clawTemplateToken.files;
      if (!files?.length) return item;
      let didReplace = false;
      const nextFiles = files.map((file) => {
        const nextPath = idByFileName.get(file.fileName);
        if (!nextPath || nextPath === file.filePath) return file;
        didReplace = true;
        return { ...file, filePath: nextPath };
      });
      if (!didReplace) return item;
      return {
        ...item,
        clawTemplateToken: {
          ...item.clawTemplateToken,
          files: nextFiles,
        },
      };
    }

    return item;
  });
};

export interface PartitionedAttachments {
  /** 真正发给后端的 ClawSendFile 列表，按 file_path 去重；quick-preview:// 已被剔除。 */
  sendingFiles: ClawSendFile[];
  /** 欢迎页暂存的本地原始 File，由调用方在创建 session / 确定目录后再上传。 */
  pendingFiles: File[];
  /** 与 pendingFiles 一一对应：保留 tempFilePath 用于真实路径替换。 */
  pendingAttachments: PendingUploadAttachment[];
}

/**
 * handleSend 之前对 attachments 做分桶：
 * - rawFile 走 pending* 通道；
 * - quick-preview://（无真实 path）整体丢弃；
 * - 普通项按 file_path 去重后塞进 sendingFiles。
 */
export const partitionAttachmentsForSend = (attachments: DraftAttachment[]): PartitionedAttachments => {
  const sendingFiles: ClawSendFile[] = [];
  const pendingFiles: File[] = [];
  const pendingAttachments: PendingUploadAttachment[] = [];
  const seen = new Set<string>();

  for (const attachment of attachments) {
    const targetFolderPath = attachment.targetFolderPath;
    if (isPendingUploadAttachment(attachment)) {
      pendingFiles.push(attachment.rawFile);
      const pendingAttachment: PendingUploadAttachment = {
        file: attachment.rawFile,
        tempFilePath: attachment.file_path,
      };
      if (targetFolderPath) {
        pendingAttachment.targetFolderPath = targetFolderPath;
      }
      pendingAttachments.push(pendingAttachment);
      continue;
    }
    const { file_path, file_type } = attachment;
    if (!file_path || seen.has(file_path)) continue;
    seen.add(file_path);
    if (file_path.startsWith('quick-preview://')) continue;
    sendingFiles.push({ file_path, file_type });
  }

  return { sendingFiles, pendingFiles, pendingAttachments };
};

export { inferFileTypeFromPath, isPendingUploadAttachment };
